// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseInputSearch,
  BaseMoreAction,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
  UnderDevelopment,
} from '~/components/common';
import { QuarantineFormModal, RepairedFormModal } from '~/components/module/inventory';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_QUARANTINE,
  EMPTY_STRING,
  EMPTY_VALUE,
} from '~/utils/constants/common';
import { IBodyRepairedQuarantine, IQuarantine } from '~/utils/interface/quarantine';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IPaginationResponse } from '~/utils/interface/base';
import { IBaseMoreAction, IHeaderCellType, IQueryParamBase, ISortBy } from '~/utils/interface/common';
import { createRepairedQuarantine, deleteQuarantine, getListQuarantine } from '~/thunks/quarantine/quarantineThunk';
import { quarantineActions, selectIsRefreshListQuarantine } from '~/thunks/quarantine/quarantineSlice';
import { QUARANTINE_PERMISSIONS } from '~/utils/constants/permissions';
import { usePermissions } from '~/utils/hooks/userPermissions';
// Styles, images, icons
import styles from './Quarantine.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsPackage = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  onRepaired: (id: string, event: MouseEvent<HTMLElement>) => void,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IQuarantine>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'inventoryName',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_name')}</Box>
          <SortComponent name='product name' sortType={getSortType('product name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuarantine) => <Box className={cx('textValue')}>{record?.inventoryName}</Box>,
    },
    {
      id: 'type',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_type')}</Box>
          <SortComponent name='type' sortType={getSortType('type')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuarantine) => (
        <Box className={cx('textValue')}>
          {record?.type ? <BaseStatus label={record?.type || EMPTY_STRING} /> : EMPTY_VALUE}
        </Box>
      ),
    },
    {
      id: 'quantity',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_quantity')}</Box>
          <SortComponent name='quantity' sortType={getSortType('quantity')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuarantine) => <Box className={cx('textValue')}>{record?.quantity}</Box>,
    },
    {
      id: 'repairedQuantity',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_repaired_quantity')}</Box>
          <SortComponent name='repaired quantity' sortType={getSortType('repaired quantity')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuarantine) => <Box className={cx('textValue')}>{record?.repairedQuantity || EMPTY_VALUE}</Box>,
    },
    {
      id: 'startDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_start_date')}</Box>
          <SortComponent name='start date' sortType={getSortType('start date')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuarantine) => <Box className={cx('textValue')}>{record?.startDate || EMPTY_VALUE}</Box>,
    },
    {
      id: 'endDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_end_date')}</Box>
          <SortComponent name='end date' sortType={getSortType('end date')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuarantine) => <Box className={cx('textValue')}>{record?.endDate || EMPTY_VALUE}</Box>,
    },
    {
      id: 'description',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_description')}</Box>
          <SortComponent name='description' sortType={getSortType('description')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuarantine) => <Box className={cx('textValue')}>{record?.description || EMPTY_VALUE}</Box>,
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IQuarantine) => {
        const actions: IBaseMoreAction[] = [
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          {
            label: t('common_action_repaired', { ns: 'common/base' }),
            icon: (
              <img
                src={icons.commonIconSettingBlack}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            onClick: (event: MouseEvent<HTMLElement>) => onRepaired(record.id, event),
          },
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onDelete(record.id),
                  hasOtherColor: true,
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Quarantine = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreateQuarantines, canViewQuarantines, canEditQuarantines, canDeleteQuarantines } =
    usePermissions(QUARANTINE_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quarantine', 'common/base']);
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  const isRefreshListQuarantine = useSelector(selectIsRefreshListQuarantine);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_QUARANTINE);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [quarantineList, setQuarantineList] = useState<IQuarantine[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isOpenQuarantineFormModal, setIsOpenQuarantineFormModal] = useState<boolean>(false);
  const [idQuarantine, setIdQuarantine] = useState<string>(EMPTY_STRING);

  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListQuarantine(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshListQuarantine) return;

    handleGetListQuarantine(paramObject);

    dispatch(quarantineActions.setRefreshQuarantineList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshListQuarantine]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListQuarantine = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getListQuarantine(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setQuarantineList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEdit = (id: string) => {
    if (!id) return;

    setIdQuarantine(id);
    setIsOpenQuarantineFormModal(true);
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdQuarantine(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdQuarantine(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleRepairedProduct = (id: string, event: MouseEvent<HTMLElement>) => {
    if (!id) return;

    setIdQuarantine(id);
    setAnchorEl(event.currentTarget);
  };

  const handleRepairedQuantity = (quantity: number) => {
    const body: IBodyRepairedQuarantine = {
      quarantineId: idQuarantine,
      repairedQuantity: String(quantity),
    };

    dispatch(quarantineActions.setRefreshQuarantineList(true));

    dispatch(createRepairedQuarantine(body))
      .unwrap()
      .then((res) => {})
      .catch((_error) => {})
      .finally(() => {
        dispatch(quarantineActions.setRefreshQuarantineList(false));
      });
  };

  const handleConfirmDelete = () => {
    dispatch(deleteQuarantine(idQuarantine))
      .unwrap()
      .then(() => {
        setIdQuarantine(EMPTY_STRING);
        handleCloseConfirmDelete();
      })
      .catch(() => {})
      .finally(() => {
        dispatch(quarantineActions.setRefreshQuarantineList(false));
      });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleShowQuarantineFormModal = () => {
    setIsOpenQuarantineFormModal((prev) => !prev);
    setIdQuarantine(EMPTY_STRING);
  };
  //#endregion Handle Function

  return (
    <Box id='quarantinePage' className={cx('container')}>
      <BaseToolbar title={t('quarantine_title')}>
        <BaseInputSearch
          width={300}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', { ns: 'common/base' })}
        />

        {canCreateQuarantines && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleShowQuarantineFormModal}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('quarantine_title_button_add')}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewQuarantines ? quarantineList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsPackage(
            t,
            sortBy,
            handleSort,
            handleEdit,
            handleDelete,
            handleRepairedProduct,
            canEditQuarantines,
            canDeleteQuarantines
          )}
          onPageChange={handlePageChange}
          {...(canEditQuarantines ? { onRowClick: handleEdit } : {})}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      <RepairedFormModal onApply={handleRepairedQuantity} anchorEl={anchorEl} open={open} onClose={handleClose} />

      {isOpenQuarantineFormModal && (
        <QuarantineFormModal idQuarantine={idQuarantine} onClose={handleShowQuarantineFormModal} />
      )}

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default Quarantine;
